// ProtectedRoute.tsx - مصحح بالكامل
import React, { useMemo, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredRole?: 'admin' | 'user';
  fallbackPath?: string;
  redirectTo?: string;
}

function ProtectedRoute({ 
  children, 
  requiredPermission, 
  requiredRole,
  fallbackPath = '/',
  redirectTo = '/login'
}: ProtectedRouteProps) {
  const location = useLocation();
  
  // استخدام useMemo لتخزين بيانات المستخدم ومنع إعادة الحساب غير الضرورية
  const user = useMemo(() => {
    const userJson = sessionStorage.getItem('user');
    if (!userJson) return null;
    
    try {
      return JSON.parse(userJson);
    } catch (error) {
      console.error('❌ خطأ في قراءة بيانات المستخدم:', error);
      // تنظيف البيانات الفاسدة
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('last_login');
      return null;
    }
  }, []);

  // التحقق من وجود token
  const token = useMemo(() => {
    return sessionStorage.getItem('token');
  }, []);

  // دوال مساعدة للتحقق من الصلاحيات
  const isAdmin = useCallback(() => {
    return user?.role === 'admin';
  }, [user]);

  const hasPermission = useCallback(() => {
    if (!requiredPermission) return true;
    return user?.[requiredPermission] === true;
  }, [user, requiredPermission]);

  const hasRequiredRole = useCallback(() => {
    if (!requiredRole) return true;
    if (requiredRole === 'admin') return isAdmin();
    // requiredRole === 'user' - أي مستخدم مسجل الدخول (مش أدمن) يعتبر user
    return !isAdmin();
  }, [requiredRole, isAdmin]);

  // إذا لا يوجد مستخدم ولا توكن، نوجه لصفحة تسجيل الدخول مع حفظ المسار الحالي
  if (!user || !token) {
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />;
  }

  // لو المستخدم أدمن، يدخل أي حاجة (حتى لو مفيش صلاحيات)
  if (isAdmin()) {
    return <>{children}</>;
  }

  // التحقق من الصلاحيات المطلوبة
  if (!hasPermission()) {
    console.warn(`🚫 مستخدم ${user.username} حاول الوصول لصفحة تتطلب صلاحية: ${requiredPermission}`);
    return <Navigate to={fallbackPath} replace />;
  }

  // التحقق من الدور المطلوب
  if (!hasRequiredRole()) {
    console.warn(`🚫 مستخدم ${user.username} حاول الوصول لصفحة تتطلب دور: ${requiredRole}`);
    return <Navigate to={fallbackPath} replace />;
  }

  // كل شيء تمام، نعرض المحتوى
  return <>{children}</>;
}

export default ProtectedRoute;